import React, { useEffect, useState } from "react";
import MarketingNav from "../../components/MarketingNav";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";
import FAQ, { faqSchema } from "../../components/FAQ";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

const highlightMap = { sentry: "Guardian", observatory: "Pro" };
const tabLabel = {
  sentry: "Sentry · Request Governance",
  observatory: "Observatory · Post-hoc",
};

const pricingFaq = [
  { q: "Do you offer a free tier?", a: "Yes. The Sentinel tier is free for up to 10,000 requests per month and includes org-wide request governance, 12 prebuilt policy packs, 90-day evidence retention, and community support. It's the right tier for proving the model out before you commit." },
  { q: "What counts as a request?", a: "A single call to /v1/gateway/evaluate — one prompt plus the policy screening and signed decision record. Streaming responses, retries, and tool calls do not multiply the count. Sentry does not charge per policy evaluated." },
  { q: "What's included in every tier?", a: "Every Sentry tier ships with the inline Gateway, SentryDSL policy authoring, signed audit bundles, drift detection on volume and denial rate, and the Seer AI root-cause analysis. Only retention, SLA, deployment model, and support escalation differ by tier." },
  { q: "How does billing work?", a: "Monthly or annual. Annual is billed up front at 20% off. Overage on request volume is billed at the next tier's effective per-million rate, never retroactively. You can set hard caps per tenant from the Settings page." },
  { q: "Can I bring my own LLM provider key?", a: "Yes. Sentry is provider-agnostic. Point your SDK at the Gateway and bring OpenAI, Anthropic, Google, Mistral, or any OpenAI-compatible endpoint. Keys stay in your KMS (BYOK) at Vanguard and above." },
  { q: "What's the deployment model for Citadel?", a: "Citadel runs in your own VPC — AWS, GCP, Azure, or on-prem Kubernetes. Citadel + Enclave additionally runs the Gateway inside an AWS Nitro Enclave for hardware-rooted trust and attestation-bound policy bundles." },
  { q: "Is there a cancellation fee?", a: "No. Monthly plans cancel at the end of the current billing period. Annual plans cancel at renewal — we do not auto-renew without a 30-day reminder email." },
  { q: "How is pricing different from a model provider?", a: "Model providers charge for inference tokens. Sentry charges for governance — the policy evaluation, signed-bundle production, and evidence storage that regulators require. You still pay your provider for the tokens, Sentry just makes those calls defensible." },
];

export default function Pricing() {
  const [data, setData] = useState({ sentry: [], observatory: [] });
  const [tab, setTab] = useState("sentry");

  useEffect(() => { api.get("/marketing/pricing").then(r => setData(r.data)).catch(() => {}); }, []);

  const tiers = data[tab] || [];
  const highlight = highlightMap[tab];
  const gridCols = tiers.length >= 5 ? "md:grid-cols-2 lg:grid-cols-5" : "md:grid-cols-3";

  return (
    <div>
      <SEO
        path="/pricing"
        title="Pricing · Sentry & Observatory"
        description="Transparent pricing. Sentinel starts free up to 10k requests/month. Scale to Guardian, Vanguard, Citadel, or Citadel + Enclave. Every tier includes signed evidence bundles and reproducible policies."
        keywords="Sentry pricing, AI governance pricing, LLM audit pricing, Observatory pricing, enterprise AI compliance pricing"
        schema={faqSchema(pricingFaq)}
      />
      <MarketingNav />
      <section className="container-x py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="chip chip-orange mb-6"><Sparkles size={12} /> Pricing</span>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-navy">Two products. Eight tiers. One audit trail.</h1>
          <p className="ink mt-4">Sentry enforces. Observatory explains. Every tier ships with signed evidence and reproducible policies.</p>
        </div>

        <div className="flex justify-center flex-wrap gap-2 mt-8">
          {["sentry", "observatory"].map(t => (
            <button key={t} data-testid={`pricing-tab-${t}`} onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
                tab === t ? "bg-navy text-cream" : "bg-[#EFEAE1] text-navy hover:bg-[#e5dfd1]"
              }`}>
              {tabLabel[t]}
            </button>
          ))}
        </div>

        <div className={`grid gap-5 mt-10 ${gridCols}`}>
          {tiers.map(t => {
            const isHi = t.name === highlight;
            return (
              <div key={t.name} data-testid={`tier-${t.name.toLowerCase().replace(/[^a-z]/g,'-')}`}
                className={`rounded-2xl p-6 flex flex-col ${
                  isHi ? "bg-navy text-cream border border-[#FF6B35]" : "bg-white border border-[rgba(17,30,51,0.08)] text-navy"
                }`}>
                {isHi && <div className="eyebrow text-[#FFD98A] mb-2">Recommended</div>}
                <div className="font-display text-xl font-bold">{t.name}</div>
                <div className={`text-3xl font-display font-bold mt-3 ${isHi ? "text-cream" : "text-navy"}`}>{t.price}</div>
                <div className={`text-xs mt-1 ${isHi ? "text-cream/60" : "ink"}`}>{t.acv}</div>
                <ul className={`space-y-2 mt-6 text-sm ${isHi ? "text-cream/85" : "ink"}`}>
                  {t.features.map(f => (
                    <li key={f} className="flex gap-2">
                      <Check size={14} className={isHi ? "text-[#FFD98A] mt-1 shrink-0" : "text-[#FF6B35] mt-1 shrink-0"} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className={`mt-6 ${isHi ? "btn-primary justify-center" : "btn-ghost justify-center"} text-sm`}>
                  {(t.name.includes("Citadel") || t.name === "Enterprise") ? "Contact sales" : "Start trial"}
                </Link>
              </div>
            );
          })}
        </div>
      </section>
      <FAQ items={pricingFaq} heading="Pricing questions, straight answers." eyebrow="FAQ" />
      <Footer />
    </div>
  );
}
